/**
 * Winclap Studio — Product Tour (spec de Learning, Andrea Guerra, 19/10/2026).
 *
 * Recorrido de onboarding para los CPs del Studio (Agentic, Brkaway, Claude). Es el
 * SEGUNDO CURSO de la infraestructura de Learning: progreso en LearningProgressTable
 * (`COURSE#studio-product-tour`), contenido versionado en ConfigTable, estados y
 * categorías de `shared/people-learning.ts`. Lo que vive acá es lo propio del Tour:
 * el contrato del contenido, quién es del Studio y el resumen de avance. Todo puro.
 */
import { z } from 'zod';
import { foldKey } from './text-normalize';
import type { LearningCategory, LearningProgress, LearningStatus } from './people-learning';

export const STUDIO_TOUR_COURSE_ID = 'studio-product-tour';

// ─── Contenido ───────────────────────────────────────────────────────────────

/**
 * Un bloque es lo que renderiza el recorrido (lectura, video, checklist, simulador…).
 * Se valida el tipo y se deja pasar el resto: el recorrido (public/studio-tour) es el
 * dueño del render; el editor estructural (tanda 3) tipa cada uno.
 */
const blockSchema = z.object({ t: z.string().min(1).max(40) }).passthrough();

const askSchema = z.object({ who: z.string().max(120), q: z.string().max(1000) });

export const tourStepSchema = z.object({
  id: z.string().min(1).max(40),
  title: z.string().max(200),
  /** Etiqueta del tipo de paso (Lectura, Video, Práctica guiada, Obligatorio…). */
  kind: z.string().max(60),
  /** Minutos estimados. */
  min: z.number().int().min(0).max(600).default(5),
  /** Parte del módulo (p. ej. 1 = recorrido, 2 = Claude + MCP). */
  part: z.number().int().min(1).max(9).optional(),
  hero: z.boolean().optional(),
  heroTitle: z.string().max(300).optional(),
  heroText: z.string().max(1000).optional(),
  blocks: z.array(blockSchema).default([]),
  /** Mensaje que Brain muestra solo al entrar por primera vez. */
  nudge: z.string().max(1000).optional(),
  /** Pista fija de Brain para este paso (fallback cuando no hay IA). */
  hint: z.string().max(1000).optional(),
  /** Nota de diseño (para quienes arman el contenido). */
  note: z.string().max(4000).optional(),
  ask: z.array(askSchema).max(20).optional(),
});
export type TourStep = z.infer<typeof tourStepSchema>;

export const tourModuleSchema = z.object({
  id: z.string().min(1).max(40),
  title: z.string().max(80),
  name: z.string().max(120),
  short: z.string().max(60),
  tag: z.string().max(120).optional(),
  card: z.string().max(400).optional(),
  sub: z.string().max(200).optional(),
  /** Nombre de cada parte del módulo, por número ("1": "Recorrido en Agentic"). */
  parts: z.record(z.string().max(120)).optional(),
  steps: z.array(tourStepSchema).min(1).max(40),
});
export type TourModule = z.infer<typeof tourModuleSchema>;

export const tourEligibilitySchema = z.object({
  /** Áreas del community map que son del Studio (comparación sin tildes ni mayúsculas). */
  areas: z.array(z.string().max(80)).max(30),
  teams: z.array(z.string().max(80)).max(50),
  /** Fragmentos del puesto (role) que marcan a un CP: "content partner", "cp lead"… */
  roleKeywords: z.array(z.string().max(60)).max(30),
  /** Personas sumadas a mano (CP Actual que cambió de área, pilotos…). */
  emails: z.array(z.string().max(120)).max(200),
  excludedEmails: z.array(z.string().max(120)).max(200),
});
export type TourEligibility = z.infer<typeof tourEligibilitySchema>;

export const tourContactsSchema = z.object({
  /** A quién escribir por Slack si no puede entrar a cada herramienta (spec 10.4). */
  agentic: z.string().max(200),
  brkaway: z.string().max(200),
  claude: z.string().max(200),
});

export const studioTourContentSchema = z
  .object({
    version: z.number().int().min(1),
    /** Fecha de lanzamiento: ingresos desde esta fecha son New CP; antes, CP Actual (spec 11). */
    launchDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    modules: z.array(tourModuleSchema).min(1).max(20),
    /** Datasets de los bloques interactivos (roles, flujo, glosario, creadoras ficticias…), por nombre. */
    data: z.record(z.unknown()).default({}),
    /** Textos editados desde la pantalla (clave = ubicación estable del texto). */
    texts: z.record(z.string().max(4000)).default({}),
    /** Notas de diseño editadas por admins, por id de paso. */
    notes: z
      .record(z.object({ note: z.string().max(4000), ask: z.array(askSchema).max(20), at: z.number().optional(), by: z.string().max(120).optional() }))
      .default({}),
    settings: z.object({
      eligibility: tourEligibilitySchema,
      contacts: tourContactsSchema,
      /** Link del Help Center de cada herramienta (los abre el recorrido). */
      helpCenters: z.object({ agentic: z.string().max(300), brkaway: z.string().max(300) }),
    }),
  })
  .superRefine((c, ctx) => {
    const mods = new Set<string>();
    const steps = new Set<string>();
    c.modules.forEach((m, mi) => {
      if (mods.has(m.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['modules', mi, 'id'], message: `Módulo repetido: ${m.id}` });
      mods.add(m.id);
      m.steps.forEach((s, si) => {
        if (steps.has(s.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['modules', mi, 'steps', si, 'id'], message: `Paso repetido: ${s.id}` });
        steps.add(s.id);
      });
    });
  });
export type StudioTourContent = z.infer<typeof studioTourContentSchema>;

export const DEFAULT_TOUR_ELIGIBILITY: TourEligibility = {
  areas: ['Creative Studio', 'Studio', 'Winclap Studio'],
  teams: [],
  roleKeywords: ['content partner', 'cp lead', 'cp senior', 'cp analyst'],
  emails: [],
  excludedEmails: [],
};

// ─── Quién es del Studio ─────────────────────────────────────────────────────

export type StudioCpRole = 'cp_lead' | 'cp_senior' | 'cp_analyst' | 'other';

export interface StudioProfile {
  /** Tiene "funciones del Studio" (spec 16): ve la pestaña y es elegible para el Tour. */
  isStudio: boolean;
  cpRole: StudioCpRole;
  matchedBy: 'email' | 'role' | 'area' | 'team' | null;
}

export interface StudioMemberLike {
  email?: string | null;
  role?: string | null;
  area?: string | null;
  team?: string | null;
}

const NOT_STUDIO: StudioProfile = { isStudio: false, cpRole: 'other', matchedBy: null };

/** Rol de CP por el puesto del community map (Lead / Senior / Analyst). */
export function cpRoleOf(role: string | null | undefined): StudioCpRole {
  const r = foldKey(role ?? '');
  if (!r) return 'other';
  if (/\blead\b/.test(r) || /\bcoord/.test(r)) return 'cp_lead';
  if (/\bsenior\b|\bsr\b/.test(r)) return 'cp_senior';
  if (/content partner|\bcp\b|analyst|analista/.test(r)) return 'cp_analyst';
  return 'other';
}

/**
 * UNA sola regla para el sidebar, el dashboard, el Brain y los recordatorios: una persona
 * es del Studio si está en la lista manual, o su puesto contiene un keyword, o su área o
 * team están en las listas. `excludedEmails` gana siempre. Comparación con `foldKey`
 * (sin tildes, sin mayúsculas, espacios normalizados).
 */
export function studioProfileOf(member: StudioMemberLike | null | undefined, rules: TourEligibility = DEFAULT_TOUR_ELIGIBILITY): StudioProfile {
  if (!member) return NOT_STUDIO;
  const email = foldKey(member.email ?? '');
  const inList = (xs: string[], v: string) => !!v && xs.some((x) => foldKey(x) === v);
  if (email && inList(rules.excludedEmails, email)) return NOT_STUDIO;
  const cpRole = cpRoleOf(member.role);
  if (email && inList(rules.emails, email)) return { isStudio: true, cpRole, matchedBy: 'email' };
  const role = foldKey(member.role ?? '');
  if (role && rules.roleKeywords.some((k) => foldKey(k) && role.includes(foldKey(k)))) return { isStudio: true, cpRole, matchedBy: 'role' };
  if (inList(rules.areas, foldKey(member.area ?? ''))) return { isStudio: true, cpRole, matchedBy: 'area' };
  if (inList(rules.teams, foldKey(member.team ?? ''))) return { isStudio: true, cpRole, matchedBy: 'team' };
  return NOT_STUDIO;
}

// ─── Estados y categorías (etiquetas propias del Tour; los valores son los de Learning) ──

export const STUDIO_STATUS_LABELS_ES: Record<LearningStatus, string> = {
  sin_iniciar: 'Sin iniciar',
  en_progreso: 'En progreso',
  completado: 'Certificado',
  no_elegible: 'No elegible',
};

export const STUDIO_CATEGORY_LABELS_ES: Record<LearningCategory, string> = {
  new_clapper: 'New CP',
  clapper_antiguo: 'CP Actual',
};

/** Id del paso de certificación (módulo 5): aprobarlo es lo que vuelve "Certificado" a la persona. */
export const TOUR_CERTIFICATION_STEP_ID = 'c1';

// ─── Resumen de avance ───────────────────────────────────────────────────────

export interface TourSummary {
  stepsTotal: number;
  stepsDone: number;
  /** 0–100, sobre los pasos del contenido vigente. */
  pct: number;
  modulesDone: string[];
  /** Primer módulo con pasos pendientes (null = todo hecho). */
  currentModuleId: string | null;
  currentModuleTitle: string | null;
  /** Primer paso pendiente (null = todo hecho). */
  currentStepId: string | null;
  certified: boolean;
  /** Minutos totales, redondeados, desde el inicio (spec 6.4). */
  elapsedMin: number;
}

export function tourSummary(content: Pick<StudioTourContent, 'modules'>, progress: Pick<LearningProgress, 'steps' | 'elapsedMs' | 'finishedAt'> | null): TourSummary {
  const done = progress?.steps ?? {};
  const modulesDone: string[] = [];
  let stepsTotal = 0;
  let stepsDone = 0;
  let current: { m: TourModule; s: TourStep } | null = null;
  for (const m of content.modules) {
    let pending = 0;
    for (const s of m.steps) {
      stepsTotal += 1;
      if (done[s.id]) stepsDone += 1;
      else {
        pending += 1;
        if (!current) current = { m, s };
      }
    }
    if (pending === 0) modulesDone.push(m.id);
  }
  const certified = !!done[TOUR_CERTIFICATION_STEP_ID] || !!progress?.finishedAt;
  return {
    stepsTotal,
    stepsDone,
    pct: stepsTotal ? Math.round((stepsDone / stepsTotal) * 100) : 0,
    modulesDone,
    currentModuleId: current?.m.id ?? null,
    currentModuleTitle: current ? `${current.m.title}: ${current.m.name}` : null,
    currentStepId: current?.s.id ?? null,
    certified,
    elapsedMin: Math.round((progress?.elapsedMs ?? 0) / 60_000),
  };
}

/** El Tour se da por terminado cuando no queda ningún paso pendiente (incluida la certificación). */
export function tourFinished(content: Pick<StudioTourContent, 'modules'>, steps: Record<string, unknown>): boolean {
  // Sin módulos no hay nada que terminar (un contenido vacío no certifica a nadie).
  return content.modules.length > 0 && content.modules.every((m) => m.steps.every((s) => !!steps[s.id]));
}

// ─── Puente iframe ⇄ host (public/studio-tour/index.html ⇄ StudioTourPage) ──

export const TOUR_MSG = {
  /** El recorrido lo repite hasta recibir `init`. */
  ready: 'st:ready',
  init: 'st:init',
  /** Evento de progreso (LearningEvent) a reportar. */
  event: 'st:event',
  /** Progreso reconciliado por el servidor. */
  refresh: 'st:refresh-progress',
  exit: 'st:exit',
  /** Edición de un texto en pantalla (solo admins). */
  saveText: 'st:save-text',
  saveNote: 'st:save-note',
  /** Pregunta a Brain desde el recorrido y su respuesta. */
  ask: 'st:ask',
  answer: 'st:answer',
} as const;

/** Tope del snapshot de interfaz que el recorrido guarda para retomar exacto (DynamoDB: ítem < 400 KB). */
export const TOUR_UI_STATE_MAX_CHARS = 60_000;
export const TOUR_ASK_MAX_CHARS = 1000;
